import { STATUS_GLYPH, STATUS_TONES } from './generated/status.js';
/**
 * Everything wrong with a set of statuses once the colour is taken away.
 *
 * Returns an empty array when the set passes. Deliberately a pure function over
 * plain data rather than a matcher over the DOM: it can then be run against the
 * real registry *and* against fixtures built to fail, which is the only way to
 * know the check is doing anything.
 */
export function colourFreeCollisions(signals) {
    const collisions = [];
    for (const signal of signals) {
        if (!signal.glyph.trim()) {
            collisions.push({
                kind: 'missing-glyph',
                tones: [signal.tone],
                detail: `${signal.tone} has no icon shape, so in grayscale it is an unlabelled colour swatch.`,
            });
        }
        if (!signal.label.trim()) {
            collisions.push({
                kind: 'missing-label',
                tones: [signal.tone],
                detail: `${signal.tone} has no written word. A bare coloured dot is not a status (§02).`,
            });
        }
    }
    collisions.push(...duplicates(signals, (signal) => signal.glyph.trim().toLowerCase(), 'duplicate-glyph', (value, tones) => `The "${value}" glyph is used by ${tones.join(' and ')}. An icon shape never means two things (§05).`));
    collisions.push(...duplicates(signals, (signal) => `${signal.glyph.trim().toLowerCase()}|${signal.label.trim().toLowerCase()}`, 'duplicate-pair', (_value, tones) => `${tones.join(' and ')} render the same icon and the same word, so with colour removed they are the same status.`));
    return collisions;
}
function duplicates(signals, keyOf, kind, describe) {
    const byKey = new Map();
    for (const signal of signals) {
        const key = keyOf(signal);
        if (!key.replace(/\|/g, '').trim())
            continue;
        byKey.set(key, [...(byKey.get(key) ?? []), signal.tone]);
    }
    return [...byKey.entries()]
        .filter(([, tones]) => tones.length > 1)
        .map(([key, tones]) => ({ kind, tones, detail: describe(key.split('|')[0] ?? key, tones) }));
}
/**
 * The default written word for each tone.
 *
 * Callers override it with the domain's own word — "No clock-out" is a warning,
 * "Auth exceeded" is blocking — but a pill always renders one of the two.
 */
export const STATUS_LABEL = {
    verified: 'Verified',
    scheduled: 'Scheduled',
    progress: 'In progress',
    warning: 'Needs review',
    critical: 'Blocking',
    inactive: 'Inactive',
};
/** The real registry, in the shape the checker reads. */
export function statusSignals() {
    return STATUS_TONES.map((tone) => ({
        tone,
        glyph: STATUS_GLYPH[tone],
        label: STATUS_LABEL[tone],
    }));
}
//# sourceMappingURL=status-signals.js.map